// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.0
// updated  : 2026-06-20
// protected: NO  -> Halaman Invoice Penagihan V2 (Parity P1-2).
// =============================================================================
// Invoice Penagihan (Billing bulanan ETAMS -> Store):
// - OWNER/KACAB: generate penagihan per store+periode (Kacab region-lock).
//   Generate ulang = versioning (versi lama SUPERSEDED, versi baru ACTIVE).
// - OWNER (semua), KACAB (region), STAFF_STORE (store sendiri, read-only): lihat & unduh PDF.
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ReceiptText, Loader2, FileText, Receipt, BellRing, AlertTriangle, CheckCircle2, Send, Search, X } from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { openPdf } from "@/v2/components/helpers";
import { TableScroll } from "@/v2/components/TableScroll";
import { ListPager, loadStoredPageSize } from "@/v2/components/ListPager";

const GEN_ROLES = ["OWNER", "KACAB_SBY", "KACAB_SMD"];
const STORE_LABEL = { STORE_AREMA: "Store Arema", STORE_BORNEO: "Store Borneo" };
const STORE_BY_REGION = { SBY: "STORE_AREMA", SMD: "STORE_BORNEO" };
const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
const fmtTs = (s) => {
  if (!s) return "-";
  try { return new Date(s).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }); }
  catch (e) { return s; }
};

export default function PenagihanPage() {
  const { me, api } = useV2Auth();
  const role = me?.role;
  const canGenerate = GEN_ROLES.includes(role);

  const now = new Date();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [detail, setDetail] = useState(null);

  // store options sesuai role
  const storeOptions = role === "OWNER"
    ? ["STORE_AREMA", "STORE_BORNEO"]
    : [STORE_BY_REGION[me?.region]].filter(Boolean);

  const [store, setStore] = useState(storeOptions[0] || "STORE_AREMA");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  // Fase 6B: Reminder Invoice Bulanan (default periode = bulan lalu).
  const prevM = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [remMonth, setRemMonth] = useState(prevM.getMonth() + 1);
  const [remYear, setRemYear] = useState(prevM.getFullYear());
  const [remData, setRemData] = useState(null);
  const [remLoading, setRemLoading] = useState(false);
  const [remSending, setRemSending] = useState(false);
  const remPeriod = `${remYear}-${String(remMonth).padStart(2, "0")}`;
  const isOwner = role === "OWNER";

  // Paginasi + pencarian (Slice S-F).
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => loadStoredPageSize("billings"));
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const listTopRef = useRef(null);
  const pageRef = useRef(1);

  const loadList = useCallback(async (p, size = pageSize) => {
    const target = p || pageRef.current || 1;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      params.set("page", target);
      params.set("page_size", size);
      const r = await api(`/api/v2/billings?${params.toString()}`);
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setBills(d.items || []);
        setMeta({ total: d.total || 0, pages: d.pages || 1 });
        setPage(d.page || target); pageRef.current = d.page || target;
      } else setBills([]);
    } finally { setLoading(false); }
  }, [api, q, from, to, pageSize]);
  // dipakai useEffect awal & sesudah generate: pertahankan halaman aktif.
  const load = useCallback(() => loadList(pageRef.current), [loadList]);
  useEffect(() => { loadList(1); }, [loadList]);

  const goPage = (p) => {
    loadList(p);
    if (listTopRef.current) listTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const changePageSize = (s) => { setPageSize(s); loadList(1, s); };
  const resetFilters = () => { setQ(""); setFrom(""); setTo(""); };

  const loadReminder = useCallback(async () => {
    setRemLoading(true);
    try {
      const r = await api(`/api/v2/invoice-reminders/status?period=${remPeriod}`);
      setRemData(r.ok ? await r.json() : null);
    } finally { setRemLoading(false); }
  }, [api, remPeriod]);
  useEffect(() => { loadReminder(); }, [loadReminder]);

  const sendReminder = async () => {
    setRemSending(true);
    try {
      const r = await api(`/api/v2/invoice-reminders/send?period=${remPeriod}`, { method: "POST" });
      const j = await r.json();
      if (!r.ok) { toast.error(j.detail || "Gagal mengirim reminder"); return; }
      if (j.email_enabled === false) {
        toast.info("Email reminder sedang DIMATIKAN (mode preview). Status in-app tetap tampil; email dinyalakan saat go-live.");
      } else {
        const sent = (j.regions || []).filter((x) => x.action === "sent").length;
        toast.success(`Reminder diproses — ${sent} region terkirim.`);
      }
    } finally { setRemSending(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api("/api/v2/billings/generate", {
        method: "POST",
        body: JSON.stringify({ store, month: Number(month), year: Number(year) }),
      });
      if (r.ok) {
        const b = await r.json();
        toast.success(`Penagihan ${b.billing_no} dibuat (v${b.version}) — Tagihan ${rupiah(b.billed_total)}`);
        load();
      } else {
        toast.error((await r.json()).detail || "Gagal generate penagihan");
      }
    } finally { setGenerating(false); }
  };

  const openDetail = async (id) => {
    const r = await api(`/api/v2/billings/${id}`);
    if (r.ok) setDetail(await r.json());
    else toast.error("Gagal memuat detail");
  };

  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  return (
    <div className="space-y-5" data-testid="v2-penagihan-page">
      <div className="flex items-center gap-2">
        <ReceiptText className="w-6 h-6 text-primary" />
        <h2 className="text-2xl font-semibold">Invoice Penagihan</h2>
      </div>

      {canGenerate && (
        <Card className="p-5 rounded-2xl" data-testid="v2-bill-form">
          <h3 className="font-semibold mb-3">Generate Penagihan Bulanan</h3>
          <p className="text-xs text-muted-foreground mb-3">
            Tagihan = Modal − (Diskon ÷ 2). Hanya transaksi penjualan (Jual); Compliment dikecualikan.
            Generate ulang periode yang sama membuat versi baru (versi lama disimpan sebagai arsip).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs">Store</Label>
              <Select value={store} onValueChange={setStore} disabled={storeOptions.length === 1}>
                <SelectTrigger data-testid="v2-bill-store"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {storeOptions.map((s) => <SelectItem key={s} value={s}>{STORE_LABEL[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Bulan</Label>
              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger data-testid="v2-bill-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tahun</Label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger data-testid="v2-bill-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Button className="w-full" onClick={generate} disabled={generating} data-testid="v2-bill-generate">
                {generating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Receipt className="w-4 h-4 mr-1" />}
                Generate
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Fase 6B: Reminder Invoice Bulanan (JUAL-only, region-aware) */}
      {canGenerate && (
        <Card className="p-5 rounded-2xl" data-testid="v2-reminder-card">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <BellRing className="w-4 h-4 text-primary" />
              <h3 className="font-semibold">Reminder Invoice Bulanan</h3>
            </div>
            {remData && (
              <Badge variant={remData.needs_billing_count > 0 ? "default" : "outline"} data-testid="v2-reminder-needs-count">
                {remData.needs_billing_count > 0
                  ? `${remData.needs_billing_count} store perlu ditagih`
                  : "Semua store sudah ditagih"}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Pengingat penagihan bulanan — hanya item <b>JUAL</b> yang terjual (MoU &amp; Compliment tidak ditagih).
            Ditujukan ke Owner/Kacab sesuai region. {remData?.email_enabled === false && (
              <span data-testid="v2-reminder-email-off">Email otomatis <b>sedang dimatikan</b> (preview) — status in-app tetap aktif.</span>
            )}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mb-4">
            <div>
              <Label className="text-xs">Bulan</Label>
              <Select value={String(remMonth)} onValueChange={(v) => setRemMonth(Number(v))}>
                <SelectTrigger data-testid="v2-reminder-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tahun</Label>
              <Select value={String(remYear)} onValueChange={(v) => setRemYear(Number(v))}>
                <SelectTrigger data-testid="v2-reminder-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {isOwner && (
              <div className="md:col-start-4">
                <Button className="w-full" variant="secondary" onClick={sendReminder} disabled={remSending} data-testid="v2-reminder-send">
                  {remSending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  Kirim Reminder
                </Button>
              </div>
            )}
          </div>

          {remLoading && !remData ? (
            <div className="text-sm text-muted-foreground py-3">Memuat status…</div>
          ) : (remData?.items?.length ? (
            <div className="space-y-2" data-testid="v2-reminder-list">
              {remData.items.map((s) => (
                <div key={s.store} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border p-3" data-testid={`v2-reminder-row-${s.store}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{s.store_label} <span className="text-[10px] text-muted-foreground">· {s.region}</span></p>
                    <p className="text-xs text-muted-foreground">
                      {s.qty} unit JUAL terjual · Rp {Number(s.gross_sales || 0).toLocaleString("id-ID")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {s.needs_billing ? (
                      <Badge variant="warning" className="gap-1" data-testid="v2-reminder-status-need">
                        <AlertTriangle className="w-3 h-3" /> Perlu ditagih
                      </Badge>
                    ) : s.has_sales ? (
                      <Badge variant="success" className="gap-1" data-testid="v2-reminder-status-done">
                        <CheckCircle2 className="w-3 h-3" /> Sudah ditagih{s.billing_no ? ` · ${s.billing_no}` : ""}
                      </Badge>
                    ) : (
                      <Badge variant="neutral" data-testid="v2-reminder-status-nosale">Tidak ada penjualan</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground py-3 text-center">Tidak ada data untuk periode ini.</p>)}
        </Card>
      )}

      <Card className="p-5 rounded-2xl" ref={listTopRef}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Daftar Penagihan</h3>
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end mb-4">
          <div>
            <Label className="text-xs">Dari Tanggal</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="billing-date-from" />
          </div>
          <div>
            <Label className="text-xs">Sampai Tanggal</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="billing-date-to" />
          </div>
          <div>
            <Label className="text-xs">Cari No. Penagihan</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="cth: BIL-2606" className="pl-8" data-testid="billing-search" />
            </div>
          </div>
          <Button variant="ghost" onClick={resetFilters} data-testid="billing-reset"><X className="w-4 h-4 mr-1" />Reset</Button>
        </div>
        <TableScroll>
          <Table>
            <TableHeader><TableRow>
              <TableHead>No</TableHead><TableHead>Store</TableHead><TableHead>Periode</TableHead>
              <TableHead>Qty</TableHead><TableHead>Total Tagihan</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {bills.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">
                  Belum ada penagihan.
                </TableCell></TableRow>
              )}
              {bills.map((b) => (
                <TableRow key={b.id} data-testid="v2-bill-row" className={b.status === "SUPERSEDED" ? "opacity-60" : ""}>
                  <TableCell className="font-mono text-xs">{b.billing_no}</TableCell>
                  <TableCell>{STORE_LABEL[b.store] || b.store}</TableCell>
                  <TableCell>{b.period}</TableCell>
                  <TableCell>{b.qty}</TableCell>
                  <TableCell className="font-medium">{rupiah(b.billed_total)}</TableCell>
                  <TableCell>
                    <Badge variant={b.status === "ACTIVE" ? "info" : "neutral"} data-testid="v2-bill-status">
                      {b.status === "ACTIVE" ? "Aktif" : "Diganti"} · v{b.version}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => openDetail(b.id)} data-testid={`v2-bill-detail-${b.id}`}>
                      Detail
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openPdf(api, "billing", b.id, b.billing_no)} data-testid="v2-bill-pdf" title="Unduh PDF">
                      <FileText className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroll>
        <ListPager
          page={page} pageSize={pageSize} total={meta.total} pages={meta.pages} loading={loading}
          onPageChange={goPage} onPageSizeChange={changePageSize}
          storageKey="billings" testid="billing-pager"
        />
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="v2-bill-detail-dialog">
          <DialogHeader><DialogTitle>Detail Penagihan {detail?.billing_no}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Store: <b>{STORE_LABEL[detail.store] || detail.store}</b></div>
                <div>Periode: <b>{detail.period}</b></div>
                <div>Status: <b>{detail.status === "ACTIVE" ? "Aktif" : "Diganti"} (v{detail.version})</b></div>
                <div>Dibuat: <b>{fmtTs(detail.created_at)}</b></div>
              </div>
              <TableScroll className="border rounded-xl">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Merchandize</TableHead><TableHead>Size</TableHead><TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Modal</TableHead><TableHead className="text-right">Diskon</TableHead>
                    <TableHead className="text-right">Beban ETAMS</TableHead><TableHead className="text-right">Tagihan</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(detail.lines || []).map((l) => (
                      <TableRow key={l.id}>
                        <TableCell>{l.product_name}</TableCell><TableCell>{l.size}</TableCell>
                        <TableCell className="text-right">{l.qty}</TableCell>
                        <TableCell className="text-right">{rupiah(l.cost)}</TableCell>
                        <TableCell className="text-right">{rupiah(l.discount)}</TableCell>
                        <TableCell className="text-right">{rupiah(l.etams_share)}</TableCell>
                        <TableCell className="text-right font-medium">{rupiah(l.billed)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableScroll>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm justify-end">
                <span>Penjualan Kotor: <b>{rupiah(detail.gross_sales)}</b></span>
                <span>Total Diskon: <b>{rupiah(detail.discount_total)}</b></span>
                <span>Beban ETAMS: <b>{rupiah(detail.etams_share)}</b></span>
                <span>Beban Store: <b>{rupiah(detail.store_share)}</b></span>
                <span className="text-base">TOTAL TAGIHAN: <b>{rupiah(detail.billed_total)}</b></span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
